import { useEffect, useState } from "react";
import { useLocale } from "../lib/i18n";
import { formatHebrewDate } from "../lib/hebrewDate";
import { getPlayerStats } from "../lib/scores";
import { supabase } from "../lib/supabase";
import type { PlayerStats } from "../lib/storage";
import { T, ScoreChip, Mark, WaveStatic, PlaylistTile } from "../components/v2";

interface HomeProps {
  onPick: (tab: "daily" | "praktis" | "privacy" | "terms") => void;
  /** Open a specific playlist's game directly (used by the discover tiles
   *  so the user doesn't bounce through the browse list first). */
  onOpenPlaylist?: (playlistId: string) => void;
}

interface DiscoverPlaylist {
  id: string;
  name: string;
  name_en: string | null;
  description: string;
  description_en: string | null;
  theme: string;
}

const THEME_VISUAL: Record<string, { color: string; accent: string; badge?: string }> = {
  chassidic:     { color: "#1a3868", accent: T.gold },
  "yamim-noraim":{ color: "#3b1f5c", accent: "#c39bff", badge: "עונתי" },
  simcha:        { color: "#5c2a2a", accent: "#f2a99c" },
  shabbos:       { color: "#5c2a2a", accent: "#f2a99c" },
  modern:        { color: "#1f4d3a", accent: "#7fd4a3" },
  kids:          { color: "#1a3868", accent: T.gold },
  kleilim:       { color: "#5c4a1f", accent: T.goldGlow, badge: "חדש" },
  vocali:        { color: "#2a4868", accent: "#9fc8ff", badge: "חדש" },
  default:       { color: T.navyMid, accent: T.gold },
};

export function Home({ onPick, onOpenPlaylist }: HomeProps) {
  const { t, locale } = useLocale();
  const he = locale === "he";
  const [stats, setStats] = useState<PlayerStats | null>(null);
  const [discover, setDiscover] = useState<DiscoverPlaylist[]>([]);

  useEffect(() => {
    getPlayerStats().then(setStats).catch(() => {});
  }, []);

  useEffect(() => {
    supabase
      .from("playlists")
      .select("id, name, name_en, description, description_en, theme")
      .eq("type", "curated")
      .order("name")
      .limit(4)
      .then(({ data }) => {
        if (data) setDiscover(data as DiscoverPlaylist[]);
      });
  }, []);

  const dateLabel = he
    ? formatHebrewDate(new Date())
    : new Date().toLocaleDateString("en-US", {
        weekday: "long",
        month: "long",
        day: "numeric",
      });

  return (
    <main
      dir={he ? "rtl" : "ltr"}
      className="px-5 pt-1 pb-2 nig-stagger"
      style={{ color: T.cream, fontFamily: T.sans }}
    >
      {/* Greeting */}
      <div style={{ marginBottom: 12 }}>
        <div
          style={{
            fontFamily: T.sans,
            fontSize: 11,
            color: T.mist,
            letterSpacing: "0.06em",
            marginBottom: 4,
          }}
        >
          {dateLabel}
        </div>
        <div
          style={{
            fontFamily: T.serif,
            fontSize: 26,
            fontWeight: 700,
            lineHeight: 1.05,
            color: T.cream,
          }}
        >
          {he ? (
            <>
              מה <span style={{ color: T.goldGlow }}>מנגנים</span> היום?
            </>
          ) : (
            <>
              What are we <span style={{ color: T.goldGlow }}>playing</span> today?
            </>
          )}
        </div>
      </div>

      {/* Hero — Daily niggun card */}
      <button
        type="button"
        onClick={() => onPick("daily")}
        className="nig-press nig-tap"
        style={{
          position: "relative",
          width: "100%",
          textAlign: "start",
          borderRadius: T.r3,
          overflow: "hidden",
          background: `linear-gradient(160deg, ${T.navyMid} 0%, ${T.navyDeep} 100%)`,
          border: `1px solid ${T.gold}30`,
          padding: "16px 18px 14px",
          boxShadow: T.shadowMed,
          marginBottom: 14,
          color: T.cream,
        }}
      >
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: -40,
            insetInlineStart: -40,
            width: 160,
            height: 160,
            background: `radial-gradient(circle, ${T.gold}55, transparent 70%)`,
            filter: "blur(20px)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            marginBottom: 10,
            position: "relative",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10.5,
                fontWeight: 600,
                letterSpacing: "0.16em",
                color: T.gold,
                marginBottom: 4,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Mark kind="spark" size={12} color={T.gold} strokeWidth={1.6} />
              {t("home.daily.label")}
            </div>
            <div
              style={{
                fontFamily: T.serif,
                fontSize: 22,
                fontWeight: 700,
                color: T.cream,
                lineHeight: 1.1,
              }}
            >
              {t("home.daily.title")}
            </div>
          </div>
          <div
            style={{
              padding: "4px 10px",
              borderRadius: 99,
              fontSize: 10.5,
              fontWeight: 600,
              background: T.gold,
              color: T.navyDeep,
              letterSpacing: "0.04em",
            }}
          >
            {t("home.daily.pill.new")}
          </div>
        </div>

        <div style={{ margin: "14px 0 10px", position: "relative" }}>
          <WaveStatic width={280} height={42} color={T.gold} opacity={0.32} />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            position: "relative",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 11.5, color: T.mist, lineHeight: 1.4 }}>
            {t("home.daily.subtitle")}
          </div>
          <div
            className="nig-press"
            style={{
              background: T.gold,
              color: T.navyDeep,
              border: 0,
              padding: "10px 18px",
              borderRadius: 99,
              fontFamily: T.sans,
              fontSize: 13,
              fontWeight: 700,
              boxShadow: T.shadowGold,
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              flexShrink: 0,
            }}
          >
            <svg width="10" height="12" viewBox="0 0 11 13" fill={T.navyDeep} aria-hidden>
              {he ? (
                <path d="M11 1 L0 6.5 L11 12 Z" />
              ) : (
                <path d="M0 1 L11 6.5 L0 12 Z" />
              )}
            </svg>
            {t("home.daily.cta")}
          </div>
        </div>
      </button>

      {/* Stats strip */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          padding: "8px 4px",
          marginBottom: 14,
          borderTop: `1px solid ${T.hairline}`,
          borderBottom: `1px solid ${T.hairline}`,
        }}
      >
        <ScoreChip
          icon={<Mark kind="streak" size={16} color={T.goldGlow} />}
          value={stats?.currentStreak ?? 0}
          label={t("home.stats.streak")}
        />
        <div
          style={{
            borderInlineStart: `1px solid ${T.hairline}`,
            borderInlineEnd: `1px solid ${T.hairline}`,
            paddingInlineStart: 16,
          }}
        >
          <ScoreChip
            value={(stats?.totalScore ?? 0).toLocaleString()}
            label={t("home.stats.points")}
          />
        </div>
        <div style={{ paddingInlineStart: 16 }}>
          <ScoreChip
            value={stats?.totalPlayed ?? 0}
            label={he ? "ניגונים שזיהיתי" : "niggunim played"}
          />
        </div>
      </div>

      {/* Section: Discover */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "baseline",
          marginBottom: 6,
        }}
      >
        <div>
          <span
            style={{
              fontFamily: T.serif,
              fontSize: 17,
              fontWeight: 700,
              color: T.cream,
            }}
          >
            {he ? "גלו" : "Discover"}
          </span>
          <span
            style={{
              fontSize: 11,
              color: T.mist,
              marginInlineStart: 8,
            }}
          >
            {he ? "פלייליסטים מומלצים" : "curated playlists"}
          </span>
        </div>
        <button
          type="button"
          onClick={() => onPick("praktis")}
          style={{
            fontSize: 11,
            color: T.gold,
            fontWeight: 500,
            background: "transparent",
            border: 0,
            padding: 0,
            cursor: "pointer",
          }}
        >
          {he ? "כל הפלייליסטים →" : "all playlists →"}
        </button>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 10,
          marginBottom: 0,
        }}
      >
        {discover.length === 0
          ? Array.from({ length: 4 }).map((_, i) => <PlaylistSkeleton key={i} />)
          : discover.map((p) => {
              const visual = THEME_VISUAL[p.theme] ?? THEME_VISUAL.default;
              const title = he ? p.name : p.name_en ?? p.name;
              const sub = he ? p.description : p.description_en ?? p.description;
              return (
                <PlaylistTile
                  key={p.id}
                  color={visual.color}
                  accent={visual.accent}
                  title={title}
                  sub={sub ?? undefined}
                  badge={visual.badge}
                  height={108}
                  onClick={() =>
                    onOpenPlaylist
                      ? onOpenPlaylist(p.id)
                      : onPick("praktis")
                  }
                />
              );
            })}
      </div>

    </main>
  );
}

function PlaylistSkeleton() {
  return (
    <div
      style={{
        height: 108,
        borderRadius: T.r2,
        background: `linear-gradient(155deg, ${T.navyMid} 0%, ${T.navyDeep} 100%)`,
        border: `1px solid ${T.hairline}`,
        opacity: 0.5,
      }}
    />
  );
}
